import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Wrench, Gauge, ShieldCheck } from "lucide-react";
import { SITE, BRANDS, STATS, SERVICES } from "../lib/site";
import BrandMarquee from "../components/BrandMarquee";
import { Seo, localBusinessSchema } from "../components/Seo";

export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo
        path="/"
        title="BMW · Audi · VW · Mercedes · Subaru · Porsche Specialists"
        description="Dirty Wrenches Automotive is a small specialist auto shop in Hermiston, OR. Owner-operated BMW, Audi, VW, Mercedes-Benz, Subaru and Porsche experts. We still work on everything else — no job is too small."
        schema={localBusinessSchema}
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1712194289451-4044105509f0?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-grain opacity-30" />

        <div className="relative w-full px-6 md:px-12 lg:px-24 pb-16 md:pb-24 pt-32 md:pt-40">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-5xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em]">// 00 — HERMISTON, OR</span>
              <span className="h-px w-16 bg-white/20" />
              <span className="font-mono-tech text-[11px] text-white/50 tracking-[0.3em] uppercase">{SITE.est}</span>
            </div>
            <h1 className="font-display font-black uppercase tracking-tighter text-6xl sm:text-7xl lg:text-8xl leading-[0.85]">
              Three mechanics.<br />
              <span className="text-[#FF3B30]">One obsession.</span><br />
              Imports.
            </h1>
            <p className="mt-8 max-w-2xl text-white/75 text-lg leading-relaxed">
              {SITE.fullName} is a three-man specialist shop. Our bread and butter is BMW, Audi, Volkswagen, Mercedes-Benz, Subaru and Porsche — but we still work on everything else, motorcycles included. No job is too small. Same three pairs of hands, every visit.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="group inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#FF5722] text-white text-xs font-bold uppercase tracking-[0.25em] px-7 py-4 transition-colors"
              >
                See what we do
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-btn"
                className="group inline-flex items-center gap-3 border border-white/30 hover:border-white text-white text-xs font-bold uppercase tracking-[0.25em] px-7 py-4 transition-colors"
              >
                Book the bay
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Stats strip */}
        <div className="absolute bottom-0 inset-x-0 hidden md:block">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="border-t border-white/10 bg-black/40 backdrop-blur-md grid grid-cols-4">
              {STATS.map((s, i) => (
                <div
                  key={s.label}
                  data-testid={`hero-stat-${i}`}
                  className={`px-6 py-5 ${i !== STATS.length - 1 ? "border-r border-white/10" : ""}`}
                >
                  <div className="font-display font-black text-4xl tracking-tighter">{s.n}</div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee />

      {/* 3-Man Shop section */}
      <section data-testid="three-man-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] bg-[#1A1F24] overflow-hidden relative">
              <img
                src="https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
                alt="Mechanic working on import car"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/80">// THE BAY</span>
                <span className="font-display font-black text-3xl">03</span>
              </div>
            </div>
            <div className="hidden lg:block absolute -right-6 -bottom-6 bg-[#FF3B30] text-white px-5 py-4">
              <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase">Mechanics</div>
              <div className="font-display font-black text-5xl leading-none">03</div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <span className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em]">// 01 — WHO WE ARE</span>
            <h2 className="mt-4 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
              A three-man shop, and proud of it.
            </h2>
            <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
              You won't get a service writer reading a script. You'll get the actual person turning the wrench. We're small on purpose — three techs, one lift apiece, every car gets looked at by all of us before it leaves.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { icon: Wrench, t: "Hands-on owner", d: "The owner is on the floor. Your invoice has a name on it." },
                { icon: Gauge, t: "Imports our specialty", d: "Six import marques we know cold — and we still take on everything else, motorcycles included." },
                { icon: ShieldCheck, t: `${SITE.warranty.months}-mo / ${(SITE.warranty.miles/1000)}k-mi warranty`, d: "Every job we do is backed by a full 12-month / 12,000-mile warranty. And photo documented start to finish." },
              ].map((b, i) => (
                <div key={b.t} data-testid={`why-card-${i}`} className="bg-[#1A1F24] border border-white/10 p-6 hover:border-[#FF3B30]/60 transition-colors">
                  <b.icon className="w-6 h-6 text-[#FF3B30]" />
                  <div className="mt-4 font-display font-bold uppercase tracking-tight text-lg">{b.t}</div>
                  <div className="mt-2 text-white/60 text-sm leading-relaxed">{b.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services preview */}
      <section data-testid="home-services-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-b border-white/10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em]">// 02 — WHAT WE DO</span>
            <h2 className="mt-4 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
              Five services.<br />Done properly.
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="home-services-cta"
            className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-white/70 hover:text-white"
          >
            Full service list <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
          {SERVICES.map((s) => (
            <Link
              key={s.code}
              to="/services"
              data-testid={`home-service-${s.code}`}
              className="group bg-[#0A0A0A] hover:bg-[#1A1F24] transition-colors p-6 lg:p-8 min-h-[220px] flex flex-col justify-between"
            >
              <span className="font-mono-tech text-xs text-[#FF3B30] tracking-[0.25em]">{s.code}</span>
              <div>
                <div className="font-display font-black uppercase tracking-tight text-xl md:text-2xl">{s.title}</div>
                <div className="mt-2 text-white/55 text-sm leading-relaxed line-clamp-3">{s.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Brands quick */}
      <section data-testid="home-brands-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em]">// 03 — MARQUES</span>
            <h2 className="mt-4 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.9]">
              The six we know inside-out.
            </h2>
            <p className="mt-6 text-white/65 leading-relaxed">
              Every brand below has its own quirks, its own scan tool, its own parts supplier. We've spent years on each of them. That's our whole offer.
            </p>
            <Link
              to="/brands"
              data-testid="home-brands-cta"
              className="mt-8 group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-white hover:text-[#FF3B30]"
            >
              See the marques <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {BRANDS.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.key}
                  data-testid={`home-brand-${b.key}`}
                  className="group bg-[#0A0A0A] hover:bg-[#1A1F24] transition-colors p-8 aspect-square flex flex-col items-center justify-center"
                >
                  <Icon className="w-16 h-16 md:w-20 md:h-20 text-white/40 group-hover:text-white transition-colors" />
                  <div className="mt-4 font-display font-bold uppercase tracking-tight text-sm text-white/60 group-hover:text-white">{b.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 stripes">
        <div className="bg-[#1A1F24] border border-white/10 p-10 md:p-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <span className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em]">// READY?</span>
              <h2 className="mt-4 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.9]">
                Got a build in mind?<br />Tell us about it.
              </h2>
              <p className="mt-5 text-white/65 max-w-xl">
                Send the mods you want, your vehicle and a window to talk — we'll call to confirm the consultation. Every job is photo documented start to finish.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link
                to="/build"
                data-testid="home-cta-btn"
                className="group inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#FF5722] text-white text-xs font-bold uppercase tracking-[0.25em] px-7 py-4 transition-colors"
              >
                Build &amp; consult
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
